import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LayoutTemplate, Shapes, Type, ImageIcon, Palette, UploadCloud, Sparkles, Layers,
  MousePointer2, ChevronLeft, X, Sun, Moon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useTheme } from "../../contexts/ThemeContext";
import MarketingCanvas from "./MarketingCanvas";
import LeftPanel from "./LeftPanel";
import FloatedToolbar from "./FloatedToolbar";
import AIPromptBar from "./AIPromptBar";
import ContextMenu from "./ContextMenu";
import ShapePopover from "./ShapePopover";

const LEFT_RAIL = [
  { id: "templates",  label: "Templates",   icon: LayoutTemplate },
  { id: "elements",   label: "Elements",    icon: Shapes },
  { id: "text",       label: "Text Styles", icon: Type },
  { id: "image",      label: "Image Styles",icon: ImageIcon },
  { id: "brand",      label: "Brand Kit",   icon: Palette },
  { id: "uploads",    label: "Uploads",     icon: UploadCloud },
  { id: "ai",         label: "AI Generate", icon: Sparkles },
  { id: "layers",     label: "Smart Layers",icon: Layers },
];

export default function MarketingDesignEditor() {
  const [activePanel, setActivePanel] = useState("templates");
  const [selection, setSelection] = useState([]); // array of element ids
  const [elements, setElements] = useState(seedElements());
  const [aiBar, setAiBar] = useState({ open: false, x: 0, y: 0 });
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, multi: false });
  const [shapePop, setShapePop] = useState({ open: false, kind: "glass", anchorRect: null });
  const boardRef = useRef(null);
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const onRailClick = (id) => setActivePanel((p) => (p === id ? null : id));

  const selectedElements = elements.filter((e) => selection.includes(e.id));
  const single = selectedElements.length === 1 ? selectedElements[0] : null;
  const toolbarType = selection.length === 1 ? single?.type : null;

  const updateElement = useCallback((id, patch) => {
    setElements((arr) => arr.map((e) => (e.id === id ? { ...e, ...patch, style: { ...e.style, ...(patch.style || {}) } } : e)));
  }, []);

  const handleSelect = (id, additive) => {
    setSelection((s) => {
      if (!additive) return [id];
      return s.includes(id) ? s.filter((x) => x !== id) : [...s, id];
    });
  };

  const clearSelection = () => {
    setSelection([]);
    setAiBar((s) => ({ ...s, open: false }));
    setCtxMenu({ open: false, x: 0, y: 0, multi: false });
  };

  // Esc to clear
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") clearSelection();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const onAction = (action, anchor) => {
    if (action === "generate") {
      const r = anchor?.getBoundingClientRect?.();
      setAiBar({ open: true, x: r ? r.left : 200, y: r ? r.bottom + 12 : 160 });
      return;
    }
    if (action === "duplicate") {
      const copies = selectedElements.map((e) => ({
        ...e,
        id: "el-" + Math.random().toString(36).slice(2, 8),
        style: { ...e.style, left: (e.style.left || 0) + 24, top: (e.style.top || 0) + 24 },
        name: (e.name || "Layer") + " Copy",
      }));
      setElements((arr) => [...arr, ...copies]);
      setSelection(copies.map((c) => c.id));
      return;
    }
    if (action === "delete") {
      setElements((arr) => arr.filter((e) => !selection.includes(e.id)));
      setSelection([]);
      return;
    }
    if (action === "lock") {
      selectedElements.forEach((e) => updateElement(e.id, { locked: !e.locked }));
      return;
    }
    if (action === "hide") {
      selectedElements.forEach((e) => updateElement(e.id, { hidden: !e.hidden }));
      return;
    }
    if (action === "layer-front")  { reorderSelected(elements, setElements, selection, "front"); return; }
    if (action === "layer-back")   { reorderSelected(elements, setElements, selection, "back");  return; }
    if (action === "layer-up")     { reorderSelected(elements, setElements, selection, "up");    return; }
    if (action === "layer-down")   { reorderSelected(elements, setElements, selection, "down");  return; }
    if (action?.startsWith?.("align-")) {
      const dir = action.replace("align-", "");
      alignSelected(elements, setElements, selection, dir);
      return;
    }
    // Other actions (appearance, effects, animate, crop, replace, etc.) → light toast for P0
    notify(actionLabel(action) + " — coming next");
  };

  const onContextMenu = (e, elementId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selection.includes(elementId)) setSelection([elementId]);
    setCtxMenu({ open: true, x: e.clientX, y: e.clientY, multi: selection.length > 1 || (selection.length === 1 && selection[0] !== elementId) });
  };

  return (
    <div
      data-testid="marketing-design-editor"
      className="flex flex-col"
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: "var(--pl-bg)",
        color: "var(--pl-text)",
      }}
    >
      {/* Topbar */}
      <header
        className="h-12 shrink-0 border-b border-[var(--pl-border)] bg-[var(--pl-surface)]/85 backdrop-blur flex items-center gap-3 px-3"
      >
        <Link
          to="/dashboard"
          data-testid="md-back"
          className="h-7 px-2 rounded-md text-[11px] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] inline-flex items-center gap-1"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Workspaces
        </Link>
        <div className="h-4 w-px bg-[var(--pl-border)]" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
          <span className="text-[var(--pl-aqua)] font-semibold">Marketing Design</span>
          <span className="opacity-50 mx-1.5">·</span>
          <span>Campaign Flyer · 960 × 540</span>
        </div>
        <div className="flex-1" />
        <button
          data-testid="md-theme-toggle"
          onClick={toggleTheme}
          className="h-7 w-7 grid place-items-center rounded-md text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] hover:bg-white/5"
          title={theme === "dark" ? "Switch to light" : "Switch to dark"}
        >
          {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 min-h-0 flex">
        {/* Left tool rail */}
        <aside
          data-testid="md-rail"
          className="w-[60px] shrink-0 border-r border-[var(--pl-border)] bg-[var(--pl-surface)]/70 backdrop-blur flex flex-col items-center gap-1.5 py-3"
        >
          <button
            data-testid="md-rail-select"
            onClick={clearSelection}
            className="h-10 w-10 grid place-items-center rounded-xl text-[var(--pl-aqua)] bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30"
            title="Select / Move"
          >
            <MousePointer2 className="h-4 w-4" />
          </button>
          <div className="w-7 h-px bg-[var(--pl-border)] my-1" />
          {LEFT_RAIL.map((item) => {
            const I = item.icon;
            const active = activePanel === item.id;
            return (
              <button
                key={item.id}
                data-testid={`md-rail-${item.id}`}
                onClick={() => onRailClick(item.id)}
                title={item.label}
                aria-label={item.label}
                aria-pressed={active}
                className={cn(
                  "h-10 w-10 grid place-items-center rounded-xl transition border",
                  active
                    ? "text-[var(--pl-aqua)] bg-[var(--pl-aqua)]/10 border-[var(--pl-aqua)]/30"
                    : "text-[var(--pl-text-muted)] border-transparent hover:text-[var(--pl-text)] hover:bg-white/5"
                )}
              >
                <I className="h-4 w-4" strokeWidth={1.7} />
              </button>
            );
          })}
        </aside>

        {/* Left content panel (Canva-style, opens beside the rail) */}
        {activePanel && (
          <LeftPanel
            id={activePanel}
            label={LEFT_RAIL.find((r) => r.id === activePanel)?.label || ""}
            onClose={() => setActivePanel(null)}
            onAddElement={(el) => {
              const id = "el-" + Math.random().toString(36).slice(2, 8);
              const placed = { ...el, id };
              setElements((arr) => [...arr, placed]);
              setSelection([id]);
            }}
          />
        )}

        {/* Canvas */}
        <MarketingCanvas
          boardRef={boardRef}
          elements={elements}
          selection={selection}
          onSelect={handleSelect}
          onClear={clearSelection}
          onContextMenu={onContextMenu}
          onMove={(id, dx, dy) => {
            // group move: if any selected, move them all; otherwise move id
            const ids = selection.includes(id) && selection.length > 0 ? selection : [id];
            setElements((arr) =>
              arr.map((e) =>
                ids.includes(e.id)
                  ? { ...e, style: { ...e.style, left: (e.style.left || 0) + dx, top: (e.style.top || 0) + dy } }
                  : e
              )
            );
          }}
        />
      </div>

      {/* Status bar */}
      <footer className="h-7 shrink-0 border-t border-[var(--pl-border)] bg-[var(--pl-surface)]/85 backdrop-blur flex items-center gap-3 px-3 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
        <span data-testid="md-status-ws" className="text-[var(--pl-aqua)]">Marketing Design</span>
        <span>·</span>
        <span>Selection: {selection.length}</span>
        <span>·</span>
        <span>Zoom 100%</span>
        <div className="flex-1" />
        <span>Frontend prototype</span>
      </footer>

      {/* Floated toolbar */}
      {toolbarType && single && !single.hidden && (
        <FloatedToolbar
          type={toolbarType}
          element={single}
          onAction={(act, anchor) => {
            if (act === "open-glass") {
              setShapePop({ open: true, kind: "glass", anchorRect: anchor?.getBoundingClientRect?.() || null });
              return;
            }
            onAction(act, anchor);
          }}
          onUpdate={(patch) => updateElement(single.id, patch)}
          onOpenPopover={(kind, anchorEl) => {
            setShapePop({
              open: true,
              kind,
              anchorRect: anchorEl?.getBoundingClientRect?.() || null,
            });
          }}
          getAnchorRect={() => {
            const node = document.querySelector(`[data-element-id="${single.id}"]`);
            return node?.getBoundingClientRect?.() || null;
          }}
        />
      )}

      {/* Shape popover */}
      {shapePop.open && single && (
        <ShapePopover
          kind={shapePop.kind}
          element={single}
          anchorRect={shapePop.anchorRect}
          onUpdate={(patch) => updateElement(single.id, patch)}
          onAction={(act) => {
            if (act === "open-glass") setShapePop({ open: true, kind: "glass", anchorRect: shapePop.anchorRect });
          }}
          onClose={() => setShapePop({ ...shapePop, open: false })}
        />
      )}

      {/* AI prompt bar */}
      {aiBar.open && (
        <AIPromptBar
          x={aiBar.x}
          y={aiBar.y}
          onClose={() => setAiBar({ ...aiBar, open: false })}
          onGenerate={(text) => {
            notify("Generating: " + (text || "(empty)").slice(0, 40));
            setAiBar({ ...aiBar, open: false });
          }}
        />
      )}

      {/* Context menu */}
      {ctxMenu.open && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          multi={ctxMenu.multi}
          smartLayersOpen={activePanel === "layers"}
          onClose={() => setCtxMenu({ open: false, x: 0, y: 0, multi: false })}
          onAction={(act) => {
            onAction(act);
            setCtxMenu({ open: false, x: 0, y: 0, multi: false });
          }}
        />
      )}

      {/* Toast container */}
      <div id="md-toast-root" />
    </div>
  );
}

/* ------------ helpers ------------ */
function reorderSelected(elements, setElements, selection, dir) {
  setElements((arr) => {
    const sel = new Set(selection);
    const ordered = [...arr];
    if (dir === "front") {
      const kept = ordered.filter((e) => !sel.has(e.id));
      const moved = ordered.filter((e) => sel.has(e.id));
      return [...kept, ...moved];
    }
    if (dir === "back") {
      const kept = ordered.filter((e) => !sel.has(e.id));
      const moved = ordered.filter((e) => sel.has(e.id));
      return [...moved, ...kept];
    }
    if (dir === "up") {
      for (let i = ordered.length - 2; i >= 0; i--) {
        if (sel.has(ordered[i].id) && !sel.has(ordered[i + 1].id)) {
          [ordered[i], ordered[i + 1]] = [ordered[i + 1], ordered[i]];
        }
      }
      return ordered;
    }
    if (dir === "down") {
      for (let i = 1; i < ordered.length; i++) {
        if (sel.has(ordered[i].id) && !sel.has(ordered[i - 1].id)) {
          [ordered[i], ordered[i - 1]] = [ordered[i - 1], ordered[i]];
        }
      }
      return ordered;
    }
    return ordered;
  });
}

function alignSelected(elements, setElements, selection, dir) {
  const BW = 960, BH = 540;
  setElements((arr) =>
    arr.map((e) => {
      if (!selection.includes(e.id)) return e;
      const s = { ...e.style };
      const w = s.width || 200, h = s.height || 60;
      if (dir === "left")   s.left = 0;
      if (dir === "right")  s.left = BW - w;
      if (dir === "center") s.left = (BW - w) / 2;
      if (dir === "top")    s.top  = 0;
      if (dir === "bottom") s.top  = BH - h;
      if (dir === "middle") s.top  = (BH - h) / 2;
      return { ...e, style: s };
    })
  );
}

function actionLabel(a) {
  const m = {
    appearance: "Appearance", effects: "Effects", animate: "Animate", crop: "Crop", replace: "Replace",
    remove: "Remove background", enhance: "AI Enhance", adjust: "Adjust", stroke: "Stroke",
    "copy-style": "Style copied", paste: "Paste", copy: "Copied", more: "More",
    layers: "Smart Layers", component: "Component", comment: "Comment", link: "Link", alt: "Alt text",
  };
  return m[a] || a;
}

function notify(text) {
  const root = document.getElementById("md-toast-root");
  if (!root) return;
  const el = document.createElement("div");
  el.textContent = text;
  el.style.cssText =
    "position:fixed;left:50%;bottom:64px;transform:translateX(-50%);z-index:6000;padding:9px 14px;border-radius:999px;" +
    "background:var(--pl-surface);border:1px solid var(--pl-aqua);color:var(--pl-text);font-weight:700;font-size:12px;" +
    "box-shadow:0 18px 44px rgba(0,0,0,.28)";
  root.appendChild(el);
  setTimeout(() => el.remove(), 1400);
}

function seedElements() {
  return [
    { id: "el-kicker",   type: "shape", name: "Campaign Badge",
      style: { left: 64,  top: 62,  width: 220, height: 32, borderRadius: 999, padding: "0 14px",
        background: "rgba(39,230,209,0.13)", border: "1px solid rgba(39,230,209,0.32)",
        color: "var(--pl-aqua)", fontSize: 11, fontWeight: 900, letterSpacing: "0.16em",
        textTransform: "uppercase", display: "inline-flex", alignItems: "center" },
      text: "Free Training Campaign" },
    { id: "el-title",    type: "text", name: "Main Headline",
      style: { left: 62, top: 110, width: 520, color: "#f4fffc", fontSize: 54, lineHeight: 0.95,
        letterSpacing: "-0.06em", fontWeight: 900 },
      text: "Launch your funding strategy call" },
    { id: "el-subtitle", type: "text", name: "Subtitle Copy",
      style: { left: 66, top: 290, width: 470, color: "rgba(244,255,252,0.74)", fontSize: 18,
        lineHeight: 1.35, fontWeight: 600 },
      text: "Flyers, banners, ads, thumbnails, social posts, and slides — one marketing workspace." },
    { id: "el-cta",      type: "shape", name: "CTA Button",
      style: { left: 66, top: 400, width: 220, height: 52, padding: "0 22px",
        borderRadius: 999, color: "#001b18", background: "linear-gradient(135deg,#27E6D1,#B9FF2F)",
        fontWeight: 900, fontSize: 14, display: "inline-flex", alignItems: "center",
        boxShadow: "0 14px 34px rgba(39,230,209,0.22)" },
      text: "Reserve Your Spot →" },
    { id: "el-photo",    type: "image", name: "Hero Image",
      style: { left: 612, top: 68, width: 282, height: 388, borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.16)",
        background:
          "radial-gradient(circle at 55% 28%, #f8d7b9 0 11%, transparent 12%)," +
          "radial-gradient(circle at 48% 24%, #3a2115 0 20%, transparent 21%)," +
          "linear-gradient(145deg, #d4b14b 0 18%, #0c1f1c 19% 100%)",
        boxShadow: "-20px 24px 0 rgba(212,177,75,0.18)" } },
    { id: "el-card",     type: "shape", name: "Glass Info Card",
      style: { left: 408, top: 348, width: 196, height: 108, padding: "14px",
        borderRadius: 18, background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.16)", backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)" },
      html: "<strong style='display:block;font-size:15px;color:#f4fffc;margin-bottom:5px'>0% APR Strategy</strong><span style='color:rgba(244,255,252,0.66);font-size:11px;line-height:1.35'>One brand system across every campaign asset.</span>" },
  ];
}
